import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ChevronDown, ChevronUp, Users } from "lucide-react";

type UCStatus = "draft" | "reviewed" | "approved";
type UCPriority = "must" | "should" | "could";

interface UseCase {
  id: string;
  ucId: string;
  name: string;
  actors: string;
  priority: UCPriority;
  status: UCStatus;
  preconditions: string;
  mainFlow: string;
  alternateFlows: string;
  exceptionFlows: string;
  postconditions: string;
  linkedReqs: string;
  notes: string;
}

const EMPTY: Omit<UseCase, "id"> = {
  ucId: "", name: "", actors: "", priority: "should", status: "draft",
  preconditions: "", mainFlow: "", alternateFlows: "", exceptionFlows: "",
  postconditions: "", linkedReqs: "", notes: "",
};

const STATUS_COLORS: Record<UCStatus, string> = {
  draft: "bg-muted text-muted-foreground border-border",
  reviewed: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400",
  approved: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:text-green-400",
};

const PRIORITY_COLORS: Record<UCPriority, string> = {
  must: "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:text-red-400",
  should: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/20 dark:text-blue-400",
  could: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/20 dark:text-amber-400",
};

export default function UseCasePanel() {
  const [useCases, setUseCases] = useState<UseCase[]>([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState<UseCase | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const nextId = () => `UC-${String(useCases.length + 1).padStart(3, "0")}`;

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, ucId: nextId() }); setFormOpen(true); };
  const openEdit = (uc: UseCase) => { setEditing(uc); setForm({ ...uc }); setFormOpen(true); };

  const save = () => {
    if (!form.name.trim()) return;
    if (editing) {
      setUseCases(prev => prev.map(uc => uc.id === editing.id ? { ...form, id: editing.id } : uc));
    } else {
      setUseCases(prev => [...prev, { ...form, id: crypto.randomUUID() }]);
    }
    setEditing(null);
    setForm(EMPTY);
    setFormOpen(false);
  };

  const del = (id: string) => setUseCases(prev => prev.filter(uc => uc.id !== id));

  const isFormOpen = formOpen;

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 border-b border-border px-5 py-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Use Cases</h2>
          <p className="text-[11px] text-muted-foreground">
            {useCases.length > 0
              ? `${useCases.length} use cases · ${useCases.filter(uc => uc.status === "approved").length} approved`
              : "Document system interactions between actors and the system in structured UML use cases."}
          </p>
        </div>
        <Button size="sm" className="h-7 gap-1 text-[11px]" onClick={openNew}><Plus className="h-3 w-3" /> Add Use Case</Button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" style={{ scrollbarWidth: "thin" }}>

        {/* Form */}
        {isFormOpen && (
          <div className="border border-border rounded-lg p-4 bg-card space-y-3">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{editing ? "Edit use case" : "New use case"}</p>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">ID</label>
                <Input value={form.ucId} onChange={e => setForm(f => ({ ...f, ucId: e.target.value }))} className="h-7 text-xs font-mono" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Use Case Name *</label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Log In and View Patient List" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as UCPriority }))}
                  className="w-full h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                  <option value="must">Must</option><option value="should">Should</option><option value="could">Could</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Actors (comma-separated)</label>
                <Input value={form.actors} onChange={e => setForm(f => ({ ...f, actors: e.target.value }))} placeholder="Nurse, Doctor, System" className="h-7 text-xs" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Linked Requirements</label>
                <Input value={form.linkedReqs} onChange={e => setForm(f => ({ ...f, linkedReqs: e.target.value }))} placeholder="REQ-001, REQ-002" className="h-7 text-xs font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Preconditions</label>
              <Input value={form.preconditions} onChange={e => setForm(f => ({ ...f, preconditions: e.target.value }))} placeholder="User is authenticated. Session is active." className="h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Main Success Flow (number each step)</label>
              <Textarea value={form.mainFlow} onChange={e => setForm(f => ({ ...f, mainFlow: e.target.value }))}
                placeholder={"1. Nurse opens the app.\n2. System displays ward patient list.\n3. Nurse selects a patient.\n4. System shows patient summary."}
                rows={5} className="text-xs font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Alternate Flows</label>
                <Textarea value={form.alternateFlows} onChange={e => setForm(f => ({ ...f, alternateFlows: e.target.value }))}
                  placeholder={"3a. Nurse searches by name.\n   3a1. System filters list in real time."} rows={3} className="text-xs font-mono" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Exception Flows</label>
                <Textarea value={form.exceptionFlows} onChange={e => setForm(f => ({ ...f, exceptionFlows: e.target.value }))}
                  placeholder={"2a. Session expired → redirect to login.\n2b. No patients in ward → display empty state."} rows={3} className="text-xs font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Postconditions</label>
              <Input value={form.postconditions} onChange={e => setForm(f => ({ ...f, postconditions: e.target.value }))} placeholder="Nurse has viewed patient summary. Audit log entry created." className="h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-muted-foreground block mb-1">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Business rules, assumptions, open questions" className="h-7 text-xs" />
            </div>
            <div className="flex items-center gap-3 justify-end">
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as UCStatus }))}
                className="h-7 text-xs border border-input rounded-md px-2 bg-background text-foreground">
                <option value="draft">Draft</option><option value="reviewed">Reviewed</option><option value="approved">Approved</option>
              </select>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(null); setForm(EMPTY); setFormOpen(false); }}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs" onClick={save}>Save</Button>
            </div>
          </div>
        )}

        {/* Use case cards */}
        {useCases.length === 0 && !isFormOpen && (
          <div className="text-center py-16 text-muted-foreground">
            <p className="text-sm font-medium">No use cases yet</p>
            <p className="text-[11px] mt-1">Each use case describes one interaction: actor → trigger → system response → outcome.</p>
          </div>
        )}

        <div className="space-y-2">
          {useCases.map(uc => {
            const isOpen = expanded === uc.id;
            const actors = uc.actors.split(",").map(a => a.trim()).filter(Boolean);
            return (
              <div key={uc.id} className="border border-border rounded-lg bg-card overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => setExpanded(isOpen ? null : uc.id)}>
                  <span className="text-[10px] font-mono text-muted-foreground w-14 shrink-0">{uc.ucId}</span>
                  <span className="text-sm font-semibold text-foreground flex-1">{uc.name}</span>
                  {actors.length > 0 && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Users className="h-3 w-3 text-muted-foreground" />
                      {actors.map(a => (
                        <span key={a} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground border border-border">{a}</span>
                      ))}
                    </div>
                  )}
                  <Badge variant="outline" className={`text-[10px] px-1.5 shrink-0 ${PRIORITY_COLORS[uc.priority]}`}>{uc.priority}</Badge>
                  <Badge variant="outline" className={`text-[10px] px-1.5 shrink-0 ${STATUS_COLORS[uc.status]}`}>{uc.status}</Badge>
                  <button onClick={e => { e.stopPropagation(); openEdit(uc); }} className="text-[10px] text-muted-foreground hover:text-foreground shrink-0">edit</button>
                  <button onClick={e => { e.stopPropagation(); del(uc.id); }} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
                </div>

                {isOpen && (
                  <div className="border-t border-border px-4 py-3 space-y-3 text-[12px]">
                    {uc.preconditions && (
                      <div><p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Preconditions</p><p>{uc.preconditions}</p></div>
                    )}
                    {uc.mainFlow && (
                      <div>
                        <p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Main Success Flow</p>
                        <pre className="font-mono text-[11px] whitespace-pre-wrap text-foreground">{uc.mainFlow}</pre>
                      </div>
                    )}
                    {(uc.alternateFlows || uc.exceptionFlows) && (
                      <div className="grid grid-cols-2 gap-4">
                        {uc.alternateFlows && (
                          <div>
                            <p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Alternate Flows</p>
                            <pre className="font-mono text-[11px] whitespace-pre-wrap text-muted-foreground">{uc.alternateFlows}</pre>
                          </div>
                        )}
                        {uc.exceptionFlows && (
                          <div>
                            <p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Exception Flows</p>
                            <pre className="font-mono text-[11px] whitespace-pre-wrap text-muted-foreground">{uc.exceptionFlows}</pre>
                          </div>
                        )}
                      </div>
                    )}
                    {uc.postconditions && (
                      <div><p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Postconditions</p><p>{uc.postconditions}</p></div>
                    )}
                    {uc.linkedReqs && (
                      <div className="flex items-center gap-1.5">
                        <p className="text-[9px] font-mono uppercase text-muted-foreground">Linked Reqs:</p>
                        {uc.linkedReqs.split(",").map(r => <Badge key={r} variant="outline" className="text-[10px] font-mono px-1.5">{r.trim()}</Badge>)}
                      </div>
                    )}
                    {uc.notes && (
                      <div><p className="text-[9px] font-mono uppercase text-muted-foreground mb-0.5">Notes</p><p className="text-muted-foreground">{uc.notes}</p></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
